import { useCallback, useEffect, useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import jobServices from '@/services/jobs';
import { FetchApiIds } from '@/types';
import { Benefit } from '@/types/api';
import JobBenefitsAndPerksComponent, {
  JobBenefitsAndPerksType,
} from './JobBenefitsAndPerks.component';

const defaultPerks = {
  label: '',
  checked: false,
  isEditing: true,
  draft: '',
};

type JobBenefitsAndPerksProps = {
  selectedBenefits?: Benefit[];
  onSelectBenefit: (benefitIds: string[]) => Promise<void>;
};

export default function JobBenefitsAndPerks({
  selectedBenefits,
  onSelectBenefit,
}: JobBenefitsAndPerksProps): JSX.Element {
  const toast = useToastContext();

  const [indexToDelete, setIndexToDelete] = useState<null | number>(null);

  const [listOfBenefitsAndPerks, setListOfBenefitsAndPerks] = useState<JobBenefitsAndPerksType[]>(
    []
  );

  const allBenefitsResponse = useApiFetch(
    FetchApiIds.BENEFITS_AND_PERKS,
    async () => jobServices.getAllBenefitsAndPerks(),
    { retry: 1 }
  );

  const createBenefitOrPerkMutation = useApiSubmit<Benefit, Partial<Benefit>>(
    jobServices.createBenefitOrPerk
  );

  const updateBenefitOrPerkMutation = useApiSubmit<Benefit, Partial<Benefit>>(
    jobServices.updateBenefitOrPerk
  );

  const deleteBenefitOrPerkMutation = useApiSubmit<void, Partial<Benefit>>(
    jobServices.deleteBenefitOrPerk
  );

  useEffect(() => {
    const selectedBenefitIds = (selectedBenefits ?? []).map((benefit) => benefit.id);

    if (allBenefitsResponse.data?.length) {
      setListOfBenefitsAndPerks(
        allBenefitsResponse.data?.map((benefit) => ({
          id: benefit.id,
          label: benefit.description,
          draft: benefit.description,
          checked: selectedBenefitIds.includes(benefit.id),
          isEditing: false,
        }))
      );
    }
  }, [allBenefitsResponse.data, selectedBenefits]);

  const getBenefitIds = (benefits: JobBenefitsAndPerksType[]): string[] => {
    return benefits.map((benefit) => benefit.id ?? '');
  };

  const onSelectPerk = useCallback(
    (index: number): void => {
      setListOfBenefitsAndPerks((prevState) => {
        const checkedBenefits = prevState.filter((perk, perkIndex) => {
          if (perkIndex === index) {
            return !perk.checked;
          }

          return perk.checked;
        });

        onSelectBenefit(getBenefitIds(checkedBenefits));

        return prevState.map((perk, perkIndex) => {
          if (perkIndex === index) {
            return { ...perk, checked: !perk.checked };
          }
          return perk;
        });
      });
    },
    [onSelectBenefit]
  );

  const isAllChecked = listOfBenefitsAndPerks.every((perk) => perk.checked);

  const onSelectAllPerks = useCallback((): void => {
    setListOfBenefitsAndPerks((prevState) => {
      const checkedBenefits = prevState.filter(() => {
        return !isAllChecked;
      });

      onSelectBenefit(getBenefitIds(checkedBenefits));

      return prevState.map((perk) => {
        return { ...perk, checked: !isAllChecked };
      });
    });
  }, [isAllChecked, onSelectBenefit]);

  const addNewPerk = useCallback((): void => {
    setListOfBenefitsAndPerks((prevState) => {
      return [...prevState, defaultPerks];
    });
  }, []);

  const onChangePerk = useCallback(
    (index: number, event: React.ChangeEvent<HTMLInputElement>): void => {
      setListOfBenefitsAndPerks((prevState) => {
        return prevState.map((perk, perkIndex) => {
          if (perkIndex === index) {
            return { ...perk, label: event.target.value };
          }
          return perk;
        });
      });
    },
    []
  );

  const onEditPerk = useCallback((index: number): void => {
    setListOfBenefitsAndPerks((prevState) => {
      return prevState.map((perk, perkIndex) => {
        if (perkIndex === index) {
          return { ...perk, isEditing: !perk.isEditing, draft: perk.label };
        }
        return perk;
      });
    });
  }, []);

  const onCancelEditingPerk = useCallback((index: number): void => {
    setListOfBenefitsAndPerks((prevState) => {
      return prevState
        .map((perk, perkIndex) => {
          if (perkIndex === index) {
            return { ...perk, isEditing: false, label: perk.draft, draft: '' };
          }
          return perk;
        })
        .filter((perk) => !!perk.label);
    });
  }, []);

  const onSavePerk = useCallback(
    async (benefit: JobBenefitsAndPerksType): Promise<void> => {
      const isAlreadyCreated = !!benefit.id;

      try {
        const mutationToCall = isAlreadyCreated
          ? updateBenefitOrPerkMutation
          : createBenefitOrPerkMutation;

        await mutationToCall.mutateAsync({
          id: benefit.id,
          description: benefit.label,
        });

        allBenefitsResponse.refetch();
        const verbToUse = isAlreadyCreated ? 'updated' : 'added';
        toast.showSuccessFeedback(`The benefit has been ${verbToUse}.`);
      } catch (err) {
        const verbToUse = isAlreadyCreated ? 'updating' : 'adding';
        toast.showErrorFeedback(`There was a problem ${verbToUse} the benefit.`);
      }
    },
    [allBenefitsResponse, createBenefitOrPerkMutation, toast, updateBenefitOrPerkMutation]
  );

  const onDeletePerk = useCallback(async (): Promise<void> => {
    if (indexToDelete === null) {
      return;
    }

    try {
      await deleteBenefitOrPerkMutation.mutateAsync({
        id: listOfBenefitsAndPerks[indexToDelete].id,
      });

      allBenefitsResponse.refetch();
      toast.showSuccessFeedback('The benefit has been deleted.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem deleting the benefit.');
    }

    setIndexToDelete(null);
  }, [
    allBenefitsResponse,
    deleteBenefitOrPerkMutation,
    indexToDelete,
    listOfBenefitsAndPerks,
    toast,
  ]);

  const onSelectPerkToDelete = useCallback((index: number): void => {
    setIndexToDelete(index);
  }, []);

  const onCancelPerkToDelete = useCallback((): void => {
    setIndexToDelete(null);
  }, []);

  return (
    <JobBenefitsAndPerksComponent
      isDeleteDialogOpen={indexToDelete !== null}
      onCancelEditingPerk={onCancelEditingPerk}
      onSelectPerkToDelete={onSelectPerkToDelete}
      onCancelPerkToDelete={onCancelPerkToDelete}
      onDeletePerk={onDeletePerk}
      onSavePerk={onSavePerk}
      onEditPerk={onEditPerk}
      onChangePerk={onChangePerk}
      addNewPerk={addNewPerk}
      onSelectAllPerks={onSelectAllPerks}
      onSelectPerk={onSelectPerk}
      listOfBenefitsAndPerks={listOfBenefitsAndPerks}
      isAllChecked={isAllChecked}
    />
  );
}
